import { useEffect, useRef } from 'react';
import type { Task } from '../types';
import { sortTasks } from '../utils/sort';
import { TaskRow } from './TaskRow';

interface Props {
  tasks: Task[];
  selectedTaskId: string | null;
  onSelect: (taskId: string) => void;
  onContextMenu?: (task: Task, e: React.MouseEvent) => void;
  onSwipeComplete?: (task: Task) => void;
  onSwipeDelete?: (task: Task) => void;
  emptyMessage?: string;
}

export function TaskList({
  tasks,
  selectedTaskId,
  onSelect,
  onContextMenu,
  onSwipeComplete,
  onSwipeDelete,
  emptyMessage,
}: Props) {
  const sorted = sortTasks(tasks);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedIdx = sorted.findIndex((t) => t.taskId === selectedTaskId);
    if (selectedIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelectorAll('.task-row')[selectedIdx];
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedTaskId, sorted]);

  if (sorted.length === 0) {
    return (
      <div className="task-list-empty">
        {emptyMessage ?? "You're caught up 🎉"}
      </div>
    );
  }

  return (
    <div className="task-list" ref={listRef} role="list">
      {sorted.map((task) => (
        <TaskRow
          key={task.taskId}
          task={task}
          selected={task.taskId === selectedTaskId}
          onSelect={() => onSelect(task.taskId)}
          onContextMenu={
            onContextMenu ? (e) => onContextMenu(task, e) : undefined
          }
          onSwipeComplete={onSwipeComplete ? () => onSwipeComplete(task) : undefined}
          onSwipeDelete={onSwipeDelete ? () => onSwipeDelete(task) : undefined}
        />
      ))}
    </div>
  );
}
